import { type Query as TCubeQuery, TimeDimension } from "@cubejs-client/core"
import {
  TMetric,
  MetricsMapping,
  GranularityMapping,
} from "~/ui-rtk/constants/metrics-mapping"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import { SortingState } from "@tanstack/react-table"
import { addFilters, addSorting } from "./cube"

export interface ExportQueryOptions {
  chartId: string
  dateRange: [Date, Date]
  granularity: AggregationType
  cubeFilters?: TCubeFilterOptions
  sorting?: SortingState
  cubeQueryParams?: Record<string, unknown>
  queryPrefix?: string | null
  maxRecords?: number
}

/**
 * Formats a date for cube queries without React context
 */
function formatDateForCube(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

/**
 * Adds time dimension with date range to cube query (without React context)
 */
function addTimeDimensionWithDateRange(
  cubeQuery: TMetric,
  dateRange: [Date, Date],
  granularity?: string,
): TCubeQuery {
  return {
    ...cubeQuery,
    timeDimensions: (cubeQuery?.timeDimensions ?? []).map(
      (timeDimension: TimeDimension) => {
        const updatedTimeDimension = {
          ...timeDimension,
          dateRange: [
            formatDateForCube(dateRange[0]),
            formatDateForCube(dateRange[1]),
          ],
        }

        if (granularity) {
          return {
            ...updatedTimeDimension,
            granularity,
          }
        }

        return updatedTimeDimension
      },
    ) as unknown as TimeDimension[],
  }
}

/**
 * Builds a cube query for CSV export without pagination
 */
export function buildExportQuery(options: ExportQueryOptions): TCubeQuery {
  const {
    chartId,
    dateRange,
    granularity,
    cubeFilters,
    sorting,
    cubeQueryParams = {},
    queryPrefix,
    maxRecords = 50000, // Safety limit to prevent memory issues
  } = options

  // Get the base query from MetricsMapping
  const mapping = MetricsMapping[chartId]
  if (!mapping) {
    throw new Error(`No metrics mapping found for chartId: ${chartId}`)
  }

  const { q } = mapping

  // Handle function-based queries
  let baseQuery: TMetric
  if (typeof q === "function") {
    const result = q(cubeQueryParams)
    // If function returns an array, use the first query for export
    baseQuery = Array.isArray(result) ? result[0] : result
  } else if (Array.isArray(q)) {
    // For array queries, use the first one for export
    baseQuery = q[0]
  } else {
    baseQuery = q
  }

  // Add time dimension with date range
  // Only add granularity if the base query already has granularity in its timeDimensions
  const hasExistingGranularity = baseQuery?.timeDimensions?.some(
    (td: TimeDimension) => td.granularity,
  )
  let query = addTimeDimensionWithDateRange(
    baseQuery,
    dateRange,
    hasExistingGranularity ? GranularityMapping[granularity] : undefined,
  )

  // Add filters if provided
  if (cubeFilters) {
    query = addFilters(query, cubeFilters, queryPrefix || undefined)
  }

  // Add sorting if provided
  if (sorting && sorting.length > 0) {
    // Check if sorting already has prefix applied (from Chart component)
    const hasPrefixApplied = sorting.some(
      sort => queryPrefix && sort.id.includes(queryPrefix),
    )
    // Only apply prefix if it's not already there
    query = addSorting(
      query,
      sorting,
      hasPrefixApplied ? undefined : queryPrefix || undefined,
    )
  }

  // Remove any existing pagination and add safety limit
  delete query.limit
  delete query.offset
  query.limit = maxRecords

  // Ensure we get the total count
  query.total = true

  return query
}

/**
 * Validates the export query to ensure it's safe to execute
 */
export function validateExportQuery(query: TCubeQuery): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  // Check if query has dimensions or measures
  if (
    (!query.dimensions || query.dimensions.length === 0) &&
    (!query.measures || query.measures.length === 0)
  ) {
    errors.push("Query must have at least one dimension or measure")
  }

  // Check for reasonable limits
  if (query.limit && query.limit > 100000) {
    errors.push("Query limit is too high (max 100,000 records)")
  }

  // Check for time dimensions (required for most exports)
  if (!query.timeDimensions || query.timeDimensions.length === 0) {
    errors.push("Query should have time dimensions for proper date filtering")
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Estimates the size of the export based on the query
 */
export function estimateExportSize(query: TCubeQuery): {
  estimatedRows: number
  estimatedSizeMB: number
  warning?: string
} {
  // Basic estimation based on dimensions and measures
  const dimensionCount = query.dimensions?.length || 0
  const measureCount = query.measures?.length || 0
  const columnCount = dimensionCount + measureCount

  // Rough estimation: 50 bytes per cell on average
  const bytesPerRow = columnCount * 50
  const estimatedRows = query.limit || 10000
  const estimatedBytes = estimatedRows * bytesPerRow
  const estimatedSizeMB = estimatedBytes / (1024 * 1024)

  let warning: string | undefined
  if (estimatedSizeMB > 50) {
    warning =
      "Large export detected. This may take several minutes to complete."
  } else if (estimatedSizeMB > 10) {
    warning = "Medium-sized export. This may take a minute to complete."
  }

  return {
    estimatedRows,
    estimatedSizeMB: Math.round(estimatedSizeMB * 100) / 100,
    warning,
  }
}
